function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from 'react';
import styled from 'styled-components';
import { white, gray100, gray400, gray800, focusShadow } from '../../theme/colors';
import { size4, CONTROL_SIZE_MAP } from '../../theme/scale';
import { fontRegular } from '../../theme/fonts';
import { DateTime } from 'luxon';
import TimePicker from 'react-time-picker';
import Select from '../select';
import InputWrapper from '../input-wrapper';
import 'react-time-picker/dist/TimePicker.css';
const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
const nowInUTC = DateTime.now().toUTC().toISO();
const AM = 'AM';
const PM = 'PM';
const UTC_ZONE = 'Z';
const AM_PM_OPTIONS = [{
  label: AM,
  value: AM
}, {
  label: PM,
  value: PM
}];
const convertTo12Hour = hours => {
  return hours % 12 || 12;
};
const convertTo24Hour = (hours, meridiem) => {
  if (meridiem === AM) {
    return hours === 12 ? 0 : hours;
  }
  return hours === 12 ? 12 : hours + 12;
};
const convertTo12HourFormat = (ISOinput, timezone) => {
  const dateTime = DateTime.fromISO(ISOinput, {
    zone: timezone
  });
  if (!dateTime.isValid || ISOinput.at(-1) !== UTC_ZONE) {
    return {
      hour: '',
      minute: '',
      amPm: '',
      valid: false
    };
  }
  let hour = dateTime.get('hour');
  const minute = dateTime.get('minute');

  // Determine 'am' or 'pm' based on the hour
  const amPm = hour >= 12 ? PM : AM;

  // Convert hours to 12-hour format
  hour = convertTo12Hour(hour);

  // Format the hour and minute as two-digit strings
  const formattedHour = hour.toString().padStart(2, '0');
  const formattedMinute = minute.toString().padStart(2, '0');

  // Create the 12-hour format object
  return {
    hour: formattedHour,
    minute: formattedMinute,
    amPm: amPm,
    valid: true
  };
};
const TimePickerWrapper = styled.div`
  display: flex;
`;
const AmPmWrapper = styled.div`
  margin-left: ${size4};
  width: 50%;
`;
const StyledTimePicker = styled.div`
  border: 1px solid ${gray400};
  border-radius: ${size4};
  background-color: ${({
  disabled
}) => disabled ? `${gray100};` : `${white};`}
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 50%;
  color: ${gray800};
  overflow: hidden;

  /* Sets font-size, padding, and height */
  ${({
  size
}) => size && {
  ...CONTROL_SIZE_MAP[size]
}}
  
  .react-time-picker__wrapper {
    border: none;
  }

  .react-time-picker__wrapper input, 
  .react-time-picker__wrapper span {
    font-family: ${fontRegular};
    color: ${gray800};
  }

  .react-time-picker__inputGroup__hour:focus,
  .react-time-picker__inputGroup__minute:focus {
    caret-color: transparent;
    outline: none;
    box-shadow: 0 0 0 3px ${focusShadow};
    border-radius: ${size4};
  }

  .react-time-picker__inputGroup__hour::selection,
  .react-time-picker__inputGroup__minute::selection {
    background-color: transparent;
  }

  .react-time-picker__inputGroup__amPm {
    display: none;
  }

  .react-time-picker__inputGroup__input:invalid {
    background: transparent;
  }
`;
export const TimePickerField = ({
  // DateTime ISO representation in UTC, example: '2022-07-21T09:35Z'
  value = nowInUTC,
  onChange,
  timezone = tz,
  timeFormatHelpText,
  disabled = false,
  size = 'md',
  id,
  label,
  smallLabel,
  hideLabel,
  hint,
  caption,
  'data-testid': dataTestId = 'time-picker-wrapper',
  ...props
}) => {
  const {
    hour,
    minute,
    amPm,
    valid
  } = convertTo12HourFormat(value, timezone);
  const handleISOChange = newValue => {
    if (!newValue) return;
    // Ensure hours in 12-hour format
    const [newHour, newMinute] = newValue.split(':');
    const twelveHour = convertTo12Hour(parseInt(newHour));
    const new24Hour = convertTo24Hour(twelveHour, amPm);
    const dateTime = DateTime.fromISO(value, {
      zone: timezone
    }).set({
      hour: new24Hour,
      minute: parseInt(newMinute)
    });
    onChange && onChange(dateTime.toUTC().toISO());
  };
  const handleAmPmChange = newValue => {
    const new24Hour = convertTo24Hour(parseInt(hour), newValue);
    const dateTime = DateTime.fromISO(value, {
      zone: timezone
    }).set({
      hour: new24Hour,
      minute: parseInt(minute)
    });
    onChange && onChange(dateTime.toUTC().toISO());
  };
  return /*#__PURE__*/React.createElement(InputWrapper, {
    id,
    label,
    hideLabel,
    hint,
    caption,
    smallLabel
  }, /*#__PURE__*/React.createElement(TimePickerWrapper, {
    id: id
  }, /*#__PURE__*/React.createElement(StyledTimePicker, {
    "data-testid": dataTestId,
    disabled: disabled || !valid,
    size: size
  }, /*#__PURE__*/React.createElement(TimePicker, _extends({
    hourAriaLabel: "time-picker-hour",
    minuteAriaLabel: "time-picker-minute"
  }, props, {
    value: valid ? `${hour}:${minute}` : '',
    onChange: handleISOChange,
    disabled: disabled || !valid,
    disableClock: true,
    clearIcon: null
  })), timeFormatHelpText && /*#__PURE__*/React.createElement("span", null, timeFormatHelpText)), /*#__PURE__*/React.createElement(AmPmWrapper, null, /*#__PURE__*/React.createElement(Select, {
    label: "AM/PM",
    hideLabel: true,
    disabled: disabled || !valid,
    value: amPm,
    onChange: value => handleAmPmChange(value),
    size: size,
    placeholder: "AM/PM",
    options: AM_PM_OPTIONS
  }))));
};
export default TimePickerField;
//# sourceMappingURL=index.js.map